import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../primitives/alert-dialog.js';
import { Alert, AlertDescription } from '../primitives/alert.js';
import { Input } from '../primitives/input.js';
import { Label } from '../primitives/label.js';
import { Spinner } from '../primitives/spinner.js';
import { buttonVariants } from '../primitives/button.js';
import { cn } from '../lib/cn.js';
import { messageOf } from './confirm.js';

export interface TypedConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  /** The exact text the user must type. */
  expected: string;
  confirmLabel: string;
  /**
   * Receives what the user typed, so the caller sends that — not `expected` —
   * and a server-side re-check compares two independent values.
   * Rejects to keep the dialog open and show the message.
   */
  onConfirm: (typed: string) => Promise<void>;
}

/**
 * A confirmation the user has to earn by typing. It exists for the one action
 * with no undo: a plain "are you sure" is answered by reflex, retyping the
 * name of the thing being destroyed is not.
 *
 * Failure handling follows `useConfirm`: a rejected action keeps the dialog
 * open and says why, and the dialog cannot be dismissed while the action runs.
 */
export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  expected,
  confirmLabel,
  onConfirm,
}: TypedConfirmProps) {
  const inputId = React.useId();
  const [typed, setTyped] = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // A reopened dialog must not remember a previous attempt's text or error.
  React.useEffect(() => {
    if (open) {
      setTyped('');
      setError(null);
    }
  }, [open]);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (running) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-2">
          <Label htmlFor={inputId}>
            Digita <code>{expected}</code> per confermare
          </Label>
          <Input
            id={inputId}
            value={typed}
            autoComplete="off"
            spellCheck={false}
            disabled={running}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        </div>

        {error && (
          <Alert tone="danger">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={running}>Annulla</AlertDialogCancel>
          <AlertDialogAction
            disabled={running || typed !== expected}
            className={cn(buttonVariants({ variant: 'danger' }))}
            onClick={(event) => {
              event.preventDefault();
              setRunning(true);
              setError(null);
              void Promise.resolve()
                .then(() => onConfirm(typed))
                .then(() => {
                  onOpenChange(false);
                })
                .catch((cause: unknown) => {
                  setError(messageOf(cause));
                })
                .finally(() => {
                  setRunning(false);
                });
            }}
          >
            {running && <Spinner className="text-current" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
